import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Group,
  Image,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { DateInput } from 'src/common/components/date/DateInput';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { JoinRequestDetail, JoinRequestStatus } from 'src/api/joinRequests';
import {
  useCompleteJoinRequest,
  useInviteUserJoinRequest,
  usePatchJoinRequest,
  useProvisionJoinRequest,
  useRejectJoinRequest,
} from './useJoinRequestMutations';

type Props = {
  requestId: string | null;
  onClose: () => void;
};

const statusBadgeColour: Record<JoinRequestStatus, string> = {
  PENDING: 'orange',
  PROVISIONED: 'blue',
  USER_INVITED: 'indigo',
  COMPLETED: 'green',
  REJECTED: 'red',
};

const statusLabel: Record<JoinRequestStatus, string> = {
  PENDING: 'Pending',
  PROVISIONED: 'Provisioned',
  USER_INVITED: 'User invited',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

type ColorPalette = {
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
};

function ColourSwatch({ label, hex }: { label: string; hex: string | undefined }) {
  if (!hex) return null;
  return (
    <Badge
      leftSection={
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: 2,
            backgroundColor: `#${hex}`,
            border: '1px solid rgba(0,0,0,0.15)',
          }}
        />
      }
      variant="light"
      color="gray"
    >
      {label} #{hex}
    </Badge>
  );
}

type ContentProps = {
  detail: JoinRequestDetail;
  onClose: () => void;
  logoUrl: string | null | undefined;
  isOpen: boolean;
  requestId: string;
};

function JoinRequestContent({ detail, onClose, logoUrl, isOpen, requestId }: ContentProps) {
  const queryClient = useQueryClient();
  const [rejectModalOpened, { open: openRejectModal, close: closeRejectModal }] =
    useDisclosure(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [sendDocuSign, setSendDocuSign] = useState(true);

  useEffect(() => {
    if (isOpen) setSendDocuSign(true);
  }, [isOpen]);

  const { patchRequest, isPending: isPatching } = usePatchJoinRequest(detail.id);
  const { rejectRequest, isPending: isRejecting } = useRejectJoinRequest();
  const { provisionRequest, isPending: isProvisioning } = useProvisionJoinRequest();
  const { inviteUserRequest, isPending: isInviting } = useInviteUserJoinRequest();
  const { completeRequest, isPending: isCompleting } = useCompleteJoinRequest();

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      companyName: detail.companyName,
      tenantName: detail.tenantName,
      frn: detail.frn ?? '',
      registeredAddress: detail.registeredAddress,
      trialEndDate: detail.trialEndDate ?? null,
    },
  });

  const isPending = detail.status === 'PENDING';

  const updateCache = (updated: JoinRequestDetail) => {
    queryClient.setQueryData(['admin', 'join-requests', requestId], updated);
  };

  const handleSave = async (values: typeof form.values) => {
    try {
      await patchRequest({
        companyName: values.companyName || undefined,
        tenantName: values.tenantName || undefined,
        frn: values.frn || null,
        registeredAddress: values.registeredAddress || undefined,
        trialEndDate: values.trialEndDate ?? null,
      });
      form.resetDirty(values);
      notifications.show({ message: 'Changes saved', color: 'green' });
    } catch (e) {
      notifyAndLogError('Failed to save', 'Could not save join request changes', e);
    }
  };

  const handleReject = async () => {
    try {
      await rejectRequest({ id: detail.id, reason: rejectionReason || undefined });
      closeRejectModal();
      notifications.show({ message: 'Join request rejected', color: 'orange' });
      onClose();
    } catch (e) {
      notifyAndLogError('Rejection failed', 'Could not reject join request', e);
    }
  };

  const handleProvision = async () => {
    try {
      const result = await provisionRequest(detail.id);
      updateCache(result.data);
      notifications.show({ message: 'Tenant provisioned', color: 'green' });
    } catch (e) {
      notifyAndLogError('Provisioning failed', 'Could not provision tenant', e);
    }
  };

  const handleInviteUser = async () => {
    try {
      const result = await inviteUserRequest(detail.id);
      updateCache(result.data);
      notifications.show({ message: 'User invited', color: 'blue' });
    } catch (e) {
      notifyAndLogError('Invite failed', 'Could not invite user', e);
    }
  };

  const handleComplete = async () => {
    try {
      const result = await completeRequest({ id: detail.id, sendDocuSign });
      updateCache(result.data);
      notifications.show({ message: 'Onboarding completed', color: 'green' });
    } catch (e) {
      notifyAndLogError('Complete failed', 'Could not complete onboarding', e);
    }
  };

  const brandingConfig = detail.brandingConfig;
  const colorPalette = brandingConfig?.color_palette as ColorPalette | undefined;
  const fontFamily = (brandingConfig?.normal as { font_family?: string } | undefined)?.font_family;

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={4}>{detail.companyName}</Title>
            <Text size="sm" c="dimmed">
              Submitted {formatDate(detail.createdAt)}
            </Text>
          </Stack>
          <Badge color={statusBadgeColour[detail.status]} variant="light" size="lg">
            {statusLabel[detail.status]}
          </Badge>
        </Group>

        {detail.rejectionReason && (
          <Text size="sm" c="red.7">
            Rejection reason: {detail.rejectionReason}
          </Text>
        )}

        <Divider label="Signatory" labelPosition="left" />
        <Group gap="md">
          <Text size="sm">
            <Text span fw={500}>
              Name:{' '}
            </Text>
            {detail.signatoryFirstName} {detail.signatoryLastName}
          </Text>
          <Text size="sm">
            <Text span fw={500}>
              Email:{' '}
            </Text>
            {detail.signatoryEmail}
          </Text>
          {detail.signatoryPosition && (
            <Text size="sm">
              <Text span fw={500}>
                Position:{' '}
              </Text>
              {detail.signatoryPosition}
            </Text>
          )}
        </Group>

        <Group gap="md">
          <Text size="sm">
            <Text span fw={500}>
              Website:{' '}
            </Text>
            {detail.websiteUrl}
          </Text>
          {detail.companyNumber && (
            <Text size="sm">
              <Text span fw={500}>
                Company number:{' '}
              </Text>
              {detail.companyNumber}
            </Text>
          )}
        </Group>

        <Divider label="Editable fields" labelPosition="left" />

        <form onSubmit={form.onSubmit(handleSave)}>
          <Stack gap="sm">
            <TextInput
              label="Company name"
              disabled={!isPending}
              {...form.getInputProps('companyName')}
            />
            <TextInput
              label="Tenant name"
              disabled={!isPending}
              {...form.getInputProps('tenantName')}
            />
            <TextInput
              label="FRN"
              placeholder="Enter FCA firm reference number"
              disabled={!isPending}
              {...form.getInputProps('frn')}
            />
            <TextInput
              label="Registered address"
              disabled={!isPending}
              {...form.getInputProps('registeredAddress')}
            />
            <DateInput
              label="Trial end date"
              disabled={!isPending}
              value={form.values.trialEndDate}
              onChange={val => form.setFieldValue('trialEndDate', val)}
            />
            {isPending && (
              <Group justify="flex-end">
                <Button type="submit" loading={isPatching} disabled={!form.isDirty()}>
                  Save changes
                </Button>
              </Group>
            )}
          </Stack>
        </form>

        {brandingConfig && (
          <>
            <Divider label="Branding" labelPosition="left" />
            <Stack gap="sm">
              {logoUrl && (
                <Image
                  src={logoUrl}
                  fit="contain"
                  h={80}
                  w="auto"
                  maw={300}
                  style={{ background: '#f8f9fa', borderRadius: 8, padding: 8 }}
                  alt="Company logo"
                />
              )}
              {colorPalette && (
                <Group gap="xs">
                  <ColourSwatch label="Primary" hex={colorPalette.primary_color} />
                  <ColourSwatch label="Secondary" hex={colorPalette.secondary_color} />
                  <ColourSwatch label="Accent" hex={colorPalette.accent_color} />
                </Group>
              )}
              {fontFamily && (
                <Text size="sm">
                  <Text span fw={500}>
                    Font:{' '}
                  </Text>
                  {fontFamily}
                </Text>
              )}
            </Stack>
          </>
        )}

        {detail.status === 'PENDING' && (
          <>
            <Divider />
            <Group justify="space-between">
              <Button
                variant="outline"
                color="red"
                onClick={openRejectModal}
                disabled={isProvisioning}
              >
                Reject
              </Button>
              <Button
                color="green"
                size="md"
                loading={isProvisioning}
                disabled={isProvisioning}
                onClick={handleProvision}
              >
                Provision tenant
              </Button>
            </Group>
          </>
        )}

        {detail.status === 'PROVISIONED' && (
          <>
            <Divider />
            <Group justify="flex-end">
              <Button
                color="blue"
                size="md"
                loading={isInviting}
                disabled={isInviting}
                onClick={handleInviteUser}
              >
                Invite user
              </Button>
            </Group>
          </>
        )}

        {detail.status === 'USER_INVITED' && (
          <>
            <Divider />
            <Stack gap="sm">
              <Checkbox
                label="Send DocuSign contract"
                checked={sendDocuSign}
                onChange={e => setSendDocuSign(e.currentTarget.checked)}
              />
              <Group justify="flex-end">
                <Button
                  color="green"
                  size="md"
                  loading={isCompleting}
                  disabled={isCompleting}
                  onClick={handleComplete}
                >
                  Complete
                </Button>
              </Group>
            </Stack>
          </>
        )}
      </Stack>

      <Modal
        opened={rejectModalOpened}
        onClose={closeRejectModal}
        title="Reject join request"
        size="sm"
      >
        <Stack gap="md">
          <Textarea
            label="Reason (optional)"
            placeholder="Enter a reason for rejection"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeRejectModal}>
              Cancel
            </Button>
            <Button color="red" loading={isRejecting} onClick={handleReject}>
              Confirm rejection
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default function JoinRequestDrawer({ requestId, onClose }: Props) {
  const { data: detail, isLoading } = useQuery<JoinRequestDetail>({
    queryKey: ['admin', 'join-requests', requestId],
    enabled: !!requestId,
  });

  const { data: logoData } = useQuery<{ logoUrl: string | null }>({
    queryKey: ['admin', 'join-requests', requestId, 'logo-url'],
    enabled: !!requestId && !!detail?.brandingConfig,
  });

  return (
    <Drawer
      opened={!!requestId}
      onClose={onClose}
      title="Join request"
      position="right"
      size="lg"
      padding="md"
    >
      {isLoading && (
        <Group justify="center" pt="xl">
          <Loader />
        </Group>
      )}
      {detail && requestId && (
        <JoinRequestContent
          key={detail.id}
          detail={detail}
          onClose={onClose}
          logoUrl={logoData?.logoUrl}
          isOpen={!!requestId}
          requestId={requestId}
        />
      )}
    </Drawer>
  );
}
